import {VetClinicPetSpecies} from "./vet-clinic-pet-species.model";
import {VetDoctorVetVisitType} from "./vet-doctor-vet-visit-type.model";

/**
 * Model definiujący pakiet usługi.
 * Zawiera domyślne ceny, reguły anulacji i dostępność, które stanowią bazę
 * do tworzenia konkretnych, przypisanych do lekarzy wariantów wizyt.
 */
export class VetineoVisitTypeBundle {

  /** Globalnie unikalny identyfikator tego konkretnego bundle. */
  public uuid: string;

  /** Identyfikator nadrzędnego pakietu.
   * Używany, gdy klinika tworzy własną, lokalną kopię globalnego pakietu
   * sieciowego, aby go nadpisać (dziedziczenie ustawień).
   */
  public vetineoVisitTypeBundleUUID: string;

  /** Identyfikator klienta. */
  public vClientId: number;

  /** Identyfikator konkretnej kliniki (jeśli pakiet jest specyficzny dla jednej placówki). */
  public vetcodeId: number;

  /** Główna nazwa usługi wyświetlana pacjentom. */
  public namePL: string;

  /** Dodatkowy dopisek do nazwy precyzujący usługę. */
  public nameAdditionPL: string;

  /** Szczegółowy opis usługi. */
  public infoPL: string;

  /** Kody i nazwy kategorii/podkategorii mapujące pakiet na główny słownik VetineoVisitTypeCategories. */
  public vetineoVisitTypeCategory: string;
  public vetineoVisitTypeCategoryName: string;
  public vetineoVisitTypeCategoryCode: string;
  public subCategoryCode: string;
  public subCategoryName: string;

  /** Czy rezerwacja tej usługi wymaga przedpłaty. */
  public prepaidRequired: boolean;

  /** Standardowa kwota przedpłaty dla tego pakietu. */
  public prepaidPrice: number;

  /** Flaga oznaczająca usługę realizowaną w ramach planów abonamentowych (Care Plans). */
  public isCarePlan: boolean;

  /** Czy pacjent ma możliwość odwołania tej usługi. */
  public termCanBeCancelled: boolean;

  /** Wymuszenie od pacjenta podania przyczyny przy anulacji. */
  public requireCancellationReason: boolean;

  /** Maksymalny czas przed terminem, do którego można bezkosztowo anulować wizytę. */
  public maxTermCancellation: number;

  /** Czy usługa odbywa się online. */
  public online: boolean;

  /** Aktywacja wbudowanych narzędzi komunikacyjnych dla tego pakietu. */
  public communicationToolsEnabled: boolean;

  /** Pozwala dołączyć pliki do rezerwacji. */
  public multimediaEnabled: boolean;

  /** Czy pacjent powinien widzieć zakładany czas trwania w interfejsie rezerwacji. */
  public showMinutes: boolean;

  /** Czy ten pakiet dotyczy każdego gatunku zwierzęcia (np. uniwersalna konsultacja). */
  public allPetSpecies: boolean;

  /** Lista konkretnych gatunków, dla których usługa jest dostępna
   */
  public servedPetSpecies: VetClinicPetSpecies[];

  /** Tablica spersonalizowanych konfiguracji (Overrides) tej usługi dla
   * poszczególnych lekarzy. To tutaj przechowywane są odstępstwa od reguł szablonu.
   */
  public vetDoctorVetVisitTypes: VetDoctorVetVisitType[];

  /**
   * Metoda fabrykująca (Factory Method).
   * Transformuje ten ogólny szablon (Bundle) w domyślny, przypisany do lekarza
   * obiekt `VetDoctorVetVisitType`.
   */
  static asVetDoctorVetVisitType(vetineoVisitTypeBundle: VetineoVisitTypeBundle): VetDoctorVetVisitType {
    let vetDoctorVetVisitType = new VetDoctorVetVisitType();
    vetDoctorVetVisitType.infoPL = vetineoVisitTypeBundle.infoPL;
    vetDoctorVetVisitType.nameAdditionPL = vetineoVisitTypeBundle.nameAdditionPL;
    vetDoctorVetVisitType.showMinutes = vetineoVisitTypeBundle.showMinutes;
    vetDoctorVetVisitType.prepaidRequired = vetineoVisitTypeBundle.prepaidRequired;
    vetDoctorVetVisitType.prepaidPrice = vetineoVisitTypeBundle.prepaidPrice;
    vetDoctorVetVisitType.online = vetineoVisitTypeBundle.online;
    vetDoctorVetVisitType.communicationToolsEnabled = vetineoVisitTypeBundle.communicationToolsEnabled;
    vetDoctorVetVisitType.multimediaEnabled = vetineoVisitTypeBundle.multimediaEnabled;
    vetDoctorVetVisitType.servedPetSpecies = vetineoVisitTypeBundle.servedPetSpecies;
    vetDoctorVetVisitType.vClientId = vetineoVisitTypeBundle.vClientId;
    vetDoctorVetVisitType.vetineoVisitTypeBundle = vetineoVisitTypeBundle;
    vetDoctorVetVisitType.vetineoVisitTypeBundleUUID = vetineoVisitTypeBundle.uuid;
    vetDoctorVetVisitType.namePL = vetineoVisitTypeBundle.namePL;
    vetDoctorVetVisitType.allPetSpecies = vetineoVisitTypeBundle.allPetSpecies;
    vetDoctorVetVisitType.requireCancellationReason = vetineoVisitTypeBundle.requireCancellationReason;
    vetDoctorVetVisitType.termCanBeCancelled = vetineoVisitTypeBundle.termCanBeCancelled;
    vetDoctorVetVisitType.maxTermCancellation = vetineoVisitTypeBundle.maxTermCancellation;
    vetDoctorVetVisitType.useBundleSettings = true;
    vetDoctorVetVisitType.isCarePlan = vetineoVisitTypeBundle.isCarePlan;

    return vetDoctorVetVisitType;
  }
}

/**
 * Główny słownik (Enum) kategoryzujący usługi weterynaryjne.
 */
export enum VetineoVisitTypeCategories {
  GENERIC,
  FIRST_VISIT,
  CONTINUITY_OF_TREATMENT,
  CONSULTATION,
  VACCINATION,
  USG,
  RTG,
  BLOOD_DRAW,
  PASSPORT,
  PERIANAL_GLAND,
  CLAWS,
  DEWORMING,
  TICKS,
  REMOVAL_OF_STITCHES,
  PRESCRIPTION,
  GROOMING_SERVICE,
  REHABILITATION,
  CARE_SERVICES,
  MEDICAL_ADVICE,
  BLOOD_PRESSURE_MEASUREMENT,
  BIOPSY
}
